import { NOTARY_KEYWORDS_TOP, NOTARY_KEYWORDS_TOP_TOTAL } from "@/content/notary-corpus/top.gen";
import { NOTARY_FAQ_COUNT_EN } from "@/content/notary-corpus/counts";
import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildServiceListSchema,
  buildSpeakableSchema,
} from "@/lib/schema";

import { NOTARY_FAQ_CATEGORIES } from "@/content/notary-corpus/faq-defs";
import { NOTARY_PROVINCE_LANDINGS } from "@/content/notary-corpus/provinces";
import { NOTARY_COUNTRY_LANDINGS } from "@/content/notary-corpus/countries";
import { NOTARY_DOC_LANDINGS } from "@/content/notary-corpus/documents";
import { getActiveProvince } from "@/provinces/_registry";

export const Route = createFileRoute("/notary/")({
  loader: async (): Promise<{ NOTARY_KEYWORDS: string[]; NOTARY_FAQ_TH: { q: string; a: string }[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/notary-corpus/th"),
      import("@/content/notary-corpus/keywords"),
    ]);
    return { NOTARY_KEYWORDS: m1.NOTARY_KEYWORDS, NOTARY_FAQ_TH: m0.NOTARY_FAQ_TH };
  },
  component: NotaryHub,
  head: ({ loaderData }) => {
    const { NOTARY_KEYWORDS = [], NOTARY_FAQ_TH = [] } = loaderData ?? {};

    const site = getActiveSiteUrl();
    const { config } = getActiveProvince();
    const title = `Notary Public ประเทศไทย 2026 — ศูนย์รวมบริการทนายรับรองเอกสาร | ${config.nameTh}`;
    const description = `ศูนย์รวมบริการ Notary Public / Notarial Services Attorney ครบวงจร — Apostille, MFA, สถานทูต, POA, Affidavit, สำเนาถูกต้อง ครอบคลุม 77 จังหวัด รวมคำถาม ${NOTARY_FAQ_TH.length}+ ข้อ อัปเดตกฎหมาย 2026`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/notary",
        type: "website",
        alternates: [{ lang: "en", path: "/en/notary" }],
        keywords: NOTARY_KEYWORDS_TOP.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Notary Public", url: `${site}/notary` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildServiceListSchema([
              { name: "Notary Public / Notarial Services Attorney", description: "รับรองลายมือชื่อ สำเนาถูกต้อง Affidavit POA โดยทนายผู้รับมอบอำนาจ", url: "/services/notary-public" },
              { name: "Apostille & Hague Legalization", description: "รับรอง Apostille สำหรับประเทศสมาชิก Hague Convention 2026", url: "/services/hague-apostille-2026" },
              { name: "MFA / Consular Legalization", description: "รับรองกงสุลกระทรวงการต่างประเทศไทย", url: "/services/mfa-legalization" },
              { name: "Certified Translation", description: "แปลรับรองพร้อมประทับตราทนาย / MOJ / NAATI", url: "/services/certified-translation" },
              { name: "Power of Attorney (Cross-Border)", description: "หนังสือมอบอำนาจข้ามประเทศ สำหรับสหรัฐ EU ออสเตรเลีย", url: "/services/notary-poa-cross-border" },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/notary")) },
      ],
    };
  },
});

const RELATED_INSIGHTS_TH = [
  { slug: "notary-public-thailand-complete", title: "Notary Public ประเทศไทย — คู่มือฉบับสมบูรณ์ 2026" },
  { slug: "notary-checklist-2026", title: "เช็คลิสต์เตรียมเอกสารก่อนไปหา Notary" },
  { slug: "apostille-thailand-2026", title: "Apostille ประเทศไทย 2026 — ประเทศที่รับ + ขั้นตอน" },
  { slug: "apostille-faq-deep-dive", title: "Apostille FAQ เจาะลึก 60 คำถาม" },
  { slug: "mfa-vs-apostille", title: "MFA vs Apostille ต่างกันอย่างไร" },
  { slug: "certified-true-copy-guide", title: "สำเนาถูกต้อง (Certified True Copy) ครบทุกกรณี" },
];

const RELATED_COMPARES = [
  { slug: "notary-public-vs-government-certification", title: "Notary Public vs การรับรองโดยรัฐ" },
  { slug: "mfa-vs-apostille", title: "MFA vs Apostille — เลือกอันไหน?" },
  { slug: "certified-true-copy-vs-notarized-copy", title: "Certified True Copy vs Notarized Copy" },
];

function NotaryHub() {
  const { NOTARY_KEYWORDS, NOTARY_FAQ_TH } = Route.useLoaderData() as { NOTARY_KEYWORDS: string[]; NOTARY_FAQ_TH: { q: string; a: string }[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <span>Notary Public</span>
        </nav>

        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Notary Public ประเทศไทย 2026 — ศูนย์รวมบริการทนายรับรองเอกสาร
          </h1>
          <p className="text-muted-foreground text-lg">
            บริการ Notarial Services Attorney ครบวงจร — รับรองลายมือชื่อ, สำเนาถูกต้อง, Affidavit,
            หนังสือมอบอำนาจ (POA), Apostille, MFA และสถานทูต โดยทนายผู้รับมอบอำนาจจากสภาทนายความ
            ครอบคลุม 77 จังหวัด อัปเดตกฎหมายปี 2026
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <a href="/services/notary-public" className="rounded-md bg-primary px-4 py-2 text-primary-foreground font-medium">ดูบริการหลัก →</a>
            <a href="/contact" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">ปรึกษาฟรี</a>
            <a href="/en/notary" className="rounded-md border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">English version →</a>
          </div>
        </header>

        <section aria-label="Notary FAQ hub" className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-bold mb-2">คำถามยอดฮิต {NOTARY_FAQ_TH.length}+ ข้อ</h2>
          <p className="text-sm text-muted-foreground mb-4">
            รวมคำถามที่ลูกค้าถามบ่อย — จัดกลุ่มตามหมวดหมู่ใช้งานจริง ตอบโดยทนายผู้รับมอบอำนาจ
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NOTARY_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/notary/faq/${c.slug}`} className="rounded-lg border bg-background p-3 text-sm hover:border-primary hover:shadow-sm transition">
                <div className="font-semibold mb-1">{c.titleTh}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
          <div className="mt-4">
            <a href="/notary/faq" className="text-sm text-primary underline">ดู FAQ ทั้งหมด →</a>
          </div>
        </section>

        <section aria-label="Notary by document type" className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-bold mb-2">รับรองเอกสารแยกตามประเภทเอกสาร</h2>
          <p className="text-sm text-muted-foreground mb-4">
            แต่ละหน้าอธิบายว่าทนายรับรองอะไร ต้องเตรียมอะไรมา จุดที่คนพลาดบ่อย และขั้นตอนนิติกรณ์ที่ต้องทำต่อ
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {NOTARY_DOC_LANDINGS.map((d) => (
              <a key={d.slug} href={`/notary/document/${d.slug}`} className="rounded-lg border bg-background p-3 text-sm hover:border-primary hover:shadow-sm transition">
                <div className="font-semibold mb-1">{d.titleTh}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{d.officialTh}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Notary by province" className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-bold mb-2">พื้นที่ให้บริการ — ทนายรับรองเอกสารแยกตามจังหวัด</h2>
          <p className="text-sm text-muted-foreground mb-4">
            แต่ละหน้าอธิบายเอกสารที่คนในพื้นที่ขอรับรองบ่อย วิธีนัดหมาย/รับ-ส่งเอกสาร และระยะเวลารวมขั้นตอนนิติกรณ์
          </p>
          <div className="flex flex-wrap gap-2 text-sm">
            {NOTARY_PROVINCE_LANDINGS.map((p) => (
              <a key={p.slug} href={`/notary/province/${p.slug}`} className="rounded-full border bg-background px-3 py-1 text-muted-foreground hover:bg-muted">
                Notary Public {p.provinceTh}
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Notary by destination country" className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-bold mb-2">แยกตามประเทศปลายทาง — เอกสารไทยไปใช้ที่ไหน</h2>
          <p className="text-sm text-muted-foreground mb-4">
            แต่ละหน้าสรุปลำดับขั้นการรับรอง ข้อกำหนดด้านภาษา จุดพลาดที่พบบ่อย และผลของอนุสัญญากรุงเฮกที่จะมีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2027
          </p>
          <div className="flex flex-wrap gap-2 text-sm">
            {NOTARY_COUNTRY_LANDINGS.map((c) => (
              <a key={c.slug} href={`/notary/country/${c.slug}`} className="rounded-full border bg-background px-3 py-1 text-muted-foreground hover:bg-muted">
                รับรองเอกสารไป{c.nameTh}
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Related services" className="mb-10">
          <h2 className="text-xl font-bold mb-3">บริการที่เกี่ยวข้อง</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { slug: "notary-public", title: "Notary Public / Notarial Services Attorney", desc: "รับรองลายมือชื่อ, สำเนาถูกต้อง, Affidavit, POA" },
              { slug: "hague-apostille-2026", title: "Apostille & Hague Legalization 2026", desc: "ไทยยื่นภาคยานุวัติสาร 30 มิ.ย. 2026 — มีผลใช้บังคับ 28 ก.พ. 2027" },
              { slug: "mfa-legalization", title: "MFA / Consular Legalization", desc: "รับรองกงสุลกระทรวงการต่างประเทศ ปกติ 3 วัน ด่วน 1 วัน" },
              { slug: "notary-poa-cross-border", title: "Power of Attorney ข้ามประเทศ", desc: "POA สำหรับใช้ในสหรัฐ, EU, ออสเตรเลีย, สิงคโปร์" },
              { slug: "certified-translation", title: "งานแปลรับรอง (ทนาย/MOJ/NAATI)", desc: "แปลรับรองพร้อมประทับตราทนาย ใช้กับ MFA และสถานทูตได้" },
              { slug: "notary-e-signature", title: "E-Signature & Remote Notary", desc: "รับรองลายเซ็นอิเล็กทรอนิกส์ตาม ETDA 2026" },
            ].map((s) => (
              <a key={s.slug} href={`/services/${s.slug}`} className="rounded-lg border bg-card p-4 hover:border-primary transition">
                <div className="font-semibold mb-1">{s.title}</div>
                <div className="text-sm text-muted-foreground">{s.desc}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Deep-dive insights" className="mb-10">
          <h2 className="text-xl font-bold mb-3">บทความเจาะลึก</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {RELATED_INSIGHTS_TH.map((it) => (
              <a key={it.slug} href={`/insights/${it.slug}`} className="rounded-lg border bg-card p-4 hover:border-primary transition">
                <div className="font-semibold text-sm">{it.title}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Compare pages" className="mb-10">
          <h2 className="text-xl font-bold mb-3">เปรียบเทียบเพื่อการตัดสินใจ</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {RELATED_COMPARES.map((c) => (
              <a key={c.slug} href={`/compare/${c.slug}`} className="rounded-lg border bg-card p-4 hover:border-primary transition">
                <div className="font-semibold text-sm">{c.title}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Provinces coverage" className="mb-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="text-xl font-bold mb-2">ครอบคลุม 77 จังหวัดทั่วประเทศ</h2>
          <p className="text-sm text-muted-foreground mb-3">
            ทีมทนายผู้รับมอบอำนาจของเราให้บริการทั่วประเทศ — รวมกรุงเทพ, เชียงใหม่, ภูเก็ต, พัทยา, ขอนแก่น,
            หาดใหญ่ และทุกจังหวัดหลัก
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {["กรุงเทพ", "เชียงใหม่", "ภูเก็ต", "ชลบุรี", "ขอนแก่น", "สงขลา", "นครราชสีมา", "อุดรธานี", "สุราษฎร์ธานี", "ระยอง"].map((p, i) => (
              <a key={i} href={`/provinces/${p}/notary-public`} className="rounded-full border bg-background px-3 py-1 hover:bg-muted">{p}</a>
            ))}
            <a href="/provinces" className="rounded-full border bg-primary text-primary-foreground px-3 py-1">ดูทั้งหมด →</a>
          </div>
        </section>

        <section aria-label="Featured questions" className="mb-10">
          <h2 className="text-xl font-bold mb-3">คำถามยอดฮิต 10 อันดับ</h2>
          <div className="space-y-3">
            {NOTARY_FAQ_TH.slice(0, 10).map((item, i) => (
              <details key={i} className="rounded-lg border bg-card p-4">
                <summary className="cursor-pointer font-semibold text-sm">
                  <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
                </summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-4">
            <a href="/notary/faq" className="text-sm text-primary underline">ดู {NOTARY_FAQ_TH.length}+ คำถามทั้งหมด →</a>
          </div>
        </section>

        <section aria-label="Keyword universe" className="mb-10">
          <h2 className="text-xl font-bold mb-3">คำค้นที่เราครอบคลุม ({NOTARY_KEYWORDS_TOP_TOTAL} คำ)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_KEYWORDS_TOP.slice(0, 120).map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">รวม {NOTARY_KEYWORDS_TOP_TOTAL} คำค้นครอบคลุมทุกมิติของบริการ Notary Public — ดูทั้งหมดที่ <a href="/notary/faq" className="text-primary underline">/notary/faq</a></p>
        </section>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">พร้อมให้บริการ 15 นาทีตอบกลับ</h2>
          <p className="text-sm text-muted-foreground mb-4">
            ปรึกษาฟรีกับทนายผู้รับมอบอำนาจ — จ.-ส. 9:00-18:00 ICT — รวมภาษาไทย/อังกฤษ/จีน/ญี่ปุ่น
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">ติดต่อทีมงาน →</a>
        </section>

        <p className="mt-6 text-xs text-muted-foreground">
          รวม {NOTARY_FAQ_COUNT_EN} คำถามเวอร์ชันภาษาอังกฤษที่ <a href="/en/notary" className="text-primary underline">/en/notary</a>
        </p>
        <PageGallery category="notary" seed="notary-hub" locale="th" limit={4} />
        <FaqSourceNote topic="notary" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}